import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { type InventoryOption } from '@/types/InventoryOption';
import { useSessionStore } from '@/store';
import { tenantReady } from './tenantReady';

export function useMoveInventoryOptions(customUrl: string | undefined = undefined) {
    const queryClient = useQueryClient();
    const session = useSessionStore();
    const customerId = computed(() => {
        if ('id' in session.getCustomer) {
            return session.getCustomer.id;
        }

        return null;
    });

    const {
        data: inventoryOptions,
        isLoading,
        isFetching,
        isFetched,
        refetch,
        error,
    } = useQuery({
        queryKey: ['inventory-options', customUrl, customerId],
        queryFn: getInventoryOptions,
        enabled: tenantReady,
        retry: false,
        staleTime: 1000 * 60, // 1 minute
    });

    async function getInventoryOptions() {
        const optionsResponse = await window.axios.get('/customers/book/inventory-options', {
            params: {
                type: 'MOVE',
                customer_id: customerId.value,
            },
        });
        let customOptions = null;
        if (customUrl) {
            const customOptionsResponse = await window.axios.get(customUrl);
            customOptions = customOptionsResponse.data.data;
        }

        let allOptions = [...optionsResponse.data.data, ...(customOptions || [])] as InventoryOption[];

        // Remove duplicates
        return allOptions.filter((option, index, self) => index === self.findIndex((t) => t.id === option.id));
    }

    function getInventoryOption(inventoryOptionId: number) {
        return inventoryOptions.value?.find((option) => option.id === inventoryOptionId);
    }

    async function createInventoryCustomOption(inventoryCustomOption: any) {
        if (!customUrl) {
            throw new Error('Custom URL is not set');
        }
        const response = await window.axios.post(customUrl, inventoryCustomOption);
        const newOption = response.data.custom_option as InventoryOption;

        // Mutate the query to add the new option
        queryClient.setQueryData(
            ['inventory-options', customUrl, customerId.value],
            (oldData: InventoryOption[] | undefined) => {
                if (!oldData) return [newOption];
                return [...oldData, newOption];
            },
        );

        return newOption;
    }

    return {
        inventoryOptions,
        isLoading,
        isFetching,
        isFetched,
        refetch,
        error,
        getInventoryOption,
        createInventoryCustomOption,
    };
}
